/**
 * App Error Boundary
 *
 * Root-level error boundary that catches critical errors in the entire application
 *
 * @module components/errors/AppErrorBoundary
 */

import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { logError } from '../../lib/logging/errorLogger';
import { addBreadcrumb } from '../../lib/logging/sentry';
import { ErrorSeverity } from '../../lib/errors/AppError';
import { Button } from '../ui/button';
import { ErrorFallback } from './ErrorFallback';
import { LanguageContext } from '@/context/LanguageContext';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * App-level Error Boundary
 *
 * Catches critical rendering errors at the root level and displays
 * a fallback UI with option to reload the application
 */
export class AppErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Add breadcrumb to track the error occurrence
    addBreadcrumb('Critical error caught by AppErrorBoundary', 'error', {
      errorMessage: error.message,
      errorName: error.name,
      hasComponentStack: !!errorInfo.componentStack,
    });

    // Log the error (this will also send to Sentry via errorLogger)
    logError(error, {
      component: 'AppErrorBoundary',
      feature: 'app',
      extra: {
        componentStack: errorInfo.componentStack,
        severity: ErrorSeverity.CRITICAL,
      },
    });

    // Update state with error info
    this.setState({
      errorInfo,
    });
  }

  handleReload = (): void => {
    // Reload the entire application
    window.location.reload();
  };

  handleReset = (): void => {
    // Try to reset without full page reload
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <LanguageContext.Consumer>
          {(ctx) => {
            const t = ctx?.t ?? ((key: string) => key);
            return (
              <ErrorFallback
                fullScreen
                title={t('errors.boundary.title')}
                description={t('errors.boundary.description')}
                details={
                  import.meta.env.DEV && this.state.error
                    ? {
                        heading: t('errors.boundary.errorDetails'),
                        message: this.state.error.message,
                        trace: this.state.errorInfo?.componentStack,
                        traceLabel: t('errors.boundary.componentStack'),
                      }
                    : undefined
                }
                actions={
                  <>
                    <Button onClick={this.handleReload}>{t('errors.boundary.reloadApp')}</Button>
                    <Button variant="outline" onClick={this.handleReset}>
                      {t('errors.boundary.tryAgain')}
                    </Button>
                  </>
                }
                footer={
                  <p className="mt-6 text-sm text-muted-foreground">
                    {t('errors.boundary.persistsPrefix')}{' '}
                    <a href="/contact" className="text-primary underline-offset-2 hover:underline">
                      {t('errors.boundary.contactSupport')}
                    </a>
                    .
                  </p>
                }
              />
            );
          }}
        </LanguageContext.Consumer>
      );
    }

    return this.props.children;
  }
}
